import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  DataTypeEnum,
  ENTITY_META_FIELDS,
  Language,
  LocalizedValueModel,
  Sort,
} from '@osos/shared/types';
import { LanguageState } from '../../state/translation/language.state';

import { Filter, Where } from '@osos/shared/types';
import { TypesUtilsService } from '../types-utils-service/types-utils.service';

@Injectable({
  providedIn: 'root',
})
export class ApiUtilsService {
  private _supportedLangs: Language[] = [];

  constructor(
    private store: Store,
    private typeUtilsService: TypesUtilsService,
  ) {
    this.store
      .select(LanguageState.supportedLangs)
      .subscribe((supportedLangs) => {
        this._supportedLangs = supportedLangs;
      });
  }

  public prepareFilter<MT extends object>(
    filter: Filter<MT>,
  ): { filter: string } {
    // Remove 'where' if all its properties are undefined or null
    if (
      filter.where &&
      Object.keys(filter.where).every(
        (k) => filter.where[k] === undefined || filter.where[k] === null,
      )
    ) {
      delete filter.where;
    }

    // If all properties of filter are undefined, null, or missing, return empty string
    const allPropsUndefined =
      Object.keys(filter).length === 0 ||
      Object.keys(filter).every(
        (k) =>
          filter[k] === undefined ||
          filter[k] === null ||
          (typeof filter[k] === 'object' &&
            filter[k] !== null &&
            Object.keys(filter[k]).length === 0),
      );

    return { filter: allPropsUndefined ? '' : JSON.stringify(filter) };
  }

  public prepareFilterCriteria<MT extends object>(
    { where, skip, limit }: Filter<MT>,
    sort?: Sort[],
  ) {
    const { filter } = this.prepareFilter<MT>({
      where,
      skip: skip,
      limit: limit,
      order: sort?.length
        ? sort.map(
            (sortField) =>
              `${
                ENTITY_META_FIELDS.some(
                  (metaField) => metaField._normalizedName == sortField.sortBy,
                )
                  ? sortField.sortBy
                  : `value.${sortField.sortBy}`
              } ${sortField.sortDir?.toUpperCase()}`,
          )
        : undefined,
    });
    return filter;
  }

  /**
   * @description Prepare filters for localized values
   * Ex: {name: {en: 'test', ar: 'test'}, id: '34234', propFilter: 'name'}
   * Output: {or: [{'name.en': 'test'}, {'name.ar': 'test'], id: {neq: '34234'}}
   */
  public prepareLocalPropFilter<T extends object>({
    name,
    id,
    propFilter = 'name',
  }: {
    name: LocalizedValueModel;
    id?: string;
    propFilter?: string;
  }): Where<T> {
    const validLocales = this.typeUtilsService.getNoneEmptyLocalesFrom(name);
    const localizedPropsWithValue = validLocales.map((key) => ({
      [`${propFilter}.${key}`]: name[key].trim(),
    }));

    const idCond = id ? { id: { neq: id } } : {};
    return {
      or: localizedPropsWithValue,
      ...idCond,
    } as Where<T>;
  }

  public prepareWhere<MT extends object>(where: Where<MT>): { where: string } {
    return { where: JSON.stringify(where) };
  }
  public prepareCountWhere<MT extends object>(
    where: Where<MT>,
    criteria?: any,
  ): string {
    return JSON.stringify({
      where: where,
      ...(criteria?.args?.length && { criteria: criteria }),
    });
  }
  private isNumericString(str: string): boolean {
    const numericRegex = /^[0-9]+$/;
    return numericRegex.test(str);
  }

  /** Used to prepare search query, to be attached with where clause.
   * @param searchTerm term to be used to search by it within the provided props
   * @param props list of props to search within them for the provided term
   */
  public prepareSearch<MT extends object>(
    searchTerm: string,
    props: string[],
    type?: DataTypeEnum,
  ): Where<MT>[] {
    if (!searchTerm || !props || !props.length) return undefined;

    return type === DataTypeEnum.Number
      ? props.map(
          (prop) =>
            ({
              [prop]: this.isNumericString(searchTerm)
                ? parseFloat(searchTerm)
                : searchTerm,
            }) as any,
        )
      : props.map(
          (prop) =>
            ({
              [prop]: {
                like: `.*${searchTerm.replace(
                  /[-[\]{}()*+?.,\\^$|#\s]/g,
                  '\\$&',
                )}.*`,
                options: 'i',
              },
            }) as any,
        );
  }

  public prepareLocalizedSearch(searchTerm: string, props: string[]) {
    const localizedProps = this.prepareFilterBy(props);
    return this.prepareSearch(searchTerm, localizedProps);
  }

  prepareFilterBy(keys: string[]): string[] {
    const result = [];
    this._supportedLangs.forEach((lang) => {
      result.push(...keys.map((key) => `${key}.${lang.languageName}`));
    });
    return result;
  }
}
